import { useCurrentProject, type ProjectRole } from '../projects/currentProject';

/** Mirror of the server-side gate (`sync/server.ts isReadOnlyRole`): only an
 *  explicit editor/owner can write. Unknown/null roles default to read-only so
 *  we never show edit affordances we can't back. */
export function isReadOnlyRole(role: ProjectRole | null): boolean {
  return role !== 'editor' && role !== 'owner';
}

/**
 * True when the current viewer can't edit this project. The server is the real
 * gate (it rejects a viewer's writes); this just stops the client showing edit
 * affordances that would silently no-op. Returns false before any project opens
 * (local/dev → editable) so the role gate only bites once a real role is known.
 */
export function useReadOnly(): boolean {
  const role = useCurrentProject((s) => s.role);
  // No project context yet (boot / local dev) → editable.
  if (role === null) return false;
  return isReadOnlyRole(role);
}

/** Imperative read for native event handlers / non-React code. Mirrors
 *  useReadOnly() (null role → editable). */
export function currentReadOnly(): boolean {
  const role = useCurrentProject.getState().role;
  return role === null ? false : isReadOnlyRole(role);
}
